import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import MoonLoader from 'react-spinners/MoonLoader';

import { LmText } from '@mes-ui/lemma';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { PageTemplateProps, savePDF } from '@progress/kendo-react-pdf';
import {
  createStudentShareLinkActions,
  getStudentDiagnosisActions,
  getStudentShareLinkListActions,
  linkSharingDrawerActions,
  updateStudentShareLinkActivityActions,
} from 'actions';
import { IChildInfo, ICreateLinkRequest, IShareLinkResponse, IUpdateLinkActivityRequest } from 'api/types';
import { AdminSectionSettingCodes, LinkExpiration, LinkExpirationId } from 'const';
import { format, isBefore } from 'date-fns';
import { useAppSelector, useBrightTheme } from 'hooks';
import { IconCopyOutline, IconDownloadFile, IconQr } from 'icons';
import { meshPortfolioLogo } from 'icons/pdf';
import { cardIllustrations, noData } from 'images';
import { StudentPortfolioPdf } from 'pages';
import UnderlinedTabs from 'portfolio3/components/common/UnderlinedTabs';
import { DrawerHeader, MobileDrawerHeader } from 'portfolio3/components/drawers';
import {
  academicYearSelectValueSelector,
  academicYearsSelector,
  chosenAcademicYearsListSelector,
  disabledDisplaySettingsSelector,
} from 'portfolio3/features/pdf/model/selectors';
import {
  emitYMEvent,
  getMetrikaEnabledLinkSettingsSections,
  getYMEventPayload,
} from 'portfolio3/features/yandexMetrika';
import { getAccentColor } from 'portfolio3/styles';
import { commonTheme } from 'portfolio3/styles/theme';
import { Switcher, SwitcherButton, toast } from 'portfolio3/ui-kit';
import { DayRangePickerController } from 'portfolio3/ui-kit/datePickers';
import { Drawer, IDrawerBase } from 'portfolio3/ui-kit/Drawer';
import { drawerContentPadding } from 'portfolio3/ui-kit/utils';
import { IRootState } from 'reducers';
import { AdminSettingsState } from 'reducers/admin/adminSettings';
import { ThemeSettingsState } from 'reducers/theme/settings';
import { adminSettingsSelector, configSelector } from 'selectors';
import { activeFormatSelector } from 'selectors/drawers';
import { getAuthToken, isFirefox, mergeSx } from 'utils';

import { GroupedActionButton, GroupedActionButtonsContainer } from '../../common/GroupedActionButtons';
import AcademicYear from './AcademicYear';
import { ActiveLinksList, ActiveLinksPopUp } from './ActiveLinks';
import { activeShareLinksSelector } from './activeShareLinksSelector';
import { getDefaultRawDisplaySettings, getSharingFormSettings } from './consts';
import Format from './Format';
import LinksOverview from './LinksOverview';
import LinksPeriod from './LinksPeriod';
import SectionsDataLoader from './SectionsDataLoader';
import Settings from './Settings';
import * as styles from './styles';
import { DynamicDisplaySettings, LinkSharingTabs } from './types';
import { mapDynamicSettingsToFullSettings } from './utils';

import './pdfStyles.scss';

interface ILinkSharingFormProps {
  isOpen: boolean;
  onClose: () => void;

  adminVisibilitySettings: AdminSettingsState;
  currentStudent: IChildInfo;
  studentShareLink?: IShareLinkResponse;
  portfolioContentLoading?: boolean;
  createStudentShareLink: typeof createStudentShareLinkActions.request;
  updateStudentShareLinkActivity: typeof updateStudentShareLinkActivityActions.request;
  getStudentShareLinkList: typeof getStudentShareLinkListActions.request;
  getStudentDiagnosis: typeof getStudentDiagnosisActions.request;
  themeSettings: ThemeSettingsState;
}

const LinkSharingForm: FC<ILinkSharingFormProps> = ({
  isOpen,
  onClose,

  createStudentShareLink,
  adminVisibilitySettings,
  currentStudent,
  studentShareLink,
  portfolioContentLoading,
  updateStudentShareLinkActivity,
  getStudentShareLinkList,
  themeSettings,
  getStudentDiagnosis,
}: ILinkSharingFormProps) => {
  const dispatch = useDispatch();
  const activeShareLinks = useAppSelector(activeShareLinksSelector);
  const academicYearSelectValue = useAppSelector(academicYearSelectValueSelector);
  const chosenAcademicYearsList = useAppSelector(chosenAcademicYearsListSelector);
  const disabledDisplaySettings = useAppSelector(disabledDisplaySettingsSelector);
  const activeFormat = useAppSelector(activeFormatSelector);
  const academicYears = useAppSelector(academicYearsSelector);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekLater = new Date(new Date().setDate(today.getDate() + 7));
  const isMobile = useMediaQuery(commonTheme.breakpoints.down('commonSm'));

  const config = useAppSelector(configSelector);
  const hideProfessionEducation = config?.HIDE_PROFESSION_EDUCATION === true;
  const hideProforientation = config?.HIDE_PROFESSION_PROFORIENTATION === true;
  const hideApplications = config?.HIDE_PROFESSION_APPLICATIONS === true;
  const hideRecommendations = config?.HIDE_PROFESSION_RECOMMENDATIONS === true;
  const hideEvents = config?.HIDE_PROFESSION_EVENTS === true;
  const hideCulture = config?.HIDE_CULTURE === true;

  const hideGia = config?.HIDE_STUDY_GIA === true;
  const hideDiagnostics = config?.HIDE_STUDY_DIAGNOSTICS === true;
  const hideTrialExam = config?.HIDE_STUDY_TRIAL_EXAM === true;
  const hideTraining = config?.HIDE_STUDY_TRAINING === true;
  const hidePractice = config?.HIDE_STUDY_PRACTICE === true;
  const hideDocuments = config?.HIDE_STUDY_DOCUMENTS === true;

  const filteredAdminVisibilitySections = useMemo(() => {
    return adminVisibilitySettings.content.sections.filter((section) => {
      if (hideCulture && section.sectionId === AdminSectionSettingCodes.culture) {
        return false;
      }

      if (hideProfessionEducation && section.sectionId === AdminSectionSettingCodes.professionEducation) {
        return false;
      }

      if (hideProforientation && section.sectionId === AdminSectionSettingCodes.professionProforientation) {
        return false;
      }

      if (hideApplications && section.sectionId === AdminSectionSettingCodes.professionApplications) {
        return false;
      }

      if (
        hideRecommendations &&
        section.sectionId === AdminSectionSettingCodes.professionProforientationRecommendations
      ) {
        return false;
      }

      if (hideEvents && section.sectionId === AdminSectionSettingCodes.professionProforientationEvents) {
        return false;
      }

      if (hideGia && section.sectionId === AdminSectionSettingCodes.gia) {
        return false;
      }

      if (hideDiagnostics && section.sectionId === AdminSectionSettingCodes.diagnosis) {
        return false;
      }

      if (hideTrialExam && section.sectionId === AdminSectionSettingCodes.trialExam) {
        return false;
      }

      if (hideTraining && section.sectionId === AdminSectionSettingCodes.studyTraining) {
        return false;
      }

      if (hidePractice && section.sectionId === AdminSectionSettingCodes.studyPractice) {
        return false;
      }

      if (hideDocuments && section.sectionId === AdminSectionSettingCodes.studyDocuments) {
        return false;
      }

      return true;
    });
  }, [
    adminVisibilitySettings.content.sections,
    hideCulture,
    hideProfessionEducation,
    hideProforientation,
    hideApplications,
    hideRecommendations,
    hideEvents,
    hideDiagnostics,
    hideDocuments,
    hideGia,
    hidePractice,
    hideTraining,
    hideTrialExam,
  ]);

  const [linkExpiration, setLinkExpiration] = useState<number>(LinkExpirationId.week);
  const [customPeriodStart, setCustomPeriodStart] = useState<Date | null>(today);
  const [customPeriodEnd, setCustomPeriodEnd] = useState<Date | null>(weekLater);
  const [isActiveLinksOpen, setActiveLinksOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(LinkSharingTabs.LINK);
  const [rawDisplaySettings, setRawDisplaySettings] = useState<DynamicDisplaySettings>(
    getDefaultRawDisplaySettings(filteredAdminVisibilitySections, disabledDisplaySettings),
  );

  const [areLinksActive, setLinksActive] = useState(false);
  const [isPdfLoading, setPdfLoading] = useState(false);
  const sharingContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainer = useRef(null);

  const isBrightTheme = useBrightTheme();
  const theme = useTheme();

  const viewSettings = getSharingFormSettings(
    isMobile,
    filteredAdminVisibilitySections,
    rawDisplaySettings,
    setRawDisplaySettings,
    disabledDisplaySettings,
  );

  const fullRawSettings = mapDynamicSettingsToFullSettings(rawDisplaySettings);

  useEffect(() => {
    if (isOpen) {
      emitYMEvent({
        type: 'shareButtonClick',
        payload: {
          linksActive: areLinksActive ? 'on' : 'off',
        },
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && currentStudent?.meshId) {
      // period = 2 - получить данные за все время
      getStudentDiagnosis(currentStudent?.meshId, 2);
    }
  }, [isOpen, currentStudent?.meshId]);

  useEffect(() => {
    setRawDisplaySettings(getDefaultRawDisplaySettings(filteredAdminVisibilitySections, disabledDisplaySettings));
  }, [filteredAdminVisibilitySections, disabledDisplaySettings]);

  useEffect(() => {
    if (!isOpen) {
      setActiveLinksOpen(false);
    } else {
      setCurrentTab(LinkSharingTabs.LINK);
    }
  }, [isOpen]);

  // при появлении дата пикера требуется проскроллить модалку вниз
  useEffect(() => {
    // получение контейнера дровера
    const drawerContentElement = sharingContainerRef.current?.parentElement;

    if (linkExpiration === LinkExpirationId.period && drawerContentElement) {
      drawerContentElement.scrollTop = drawerContentElement.scrollHeight;
    }
  }, [linkExpiration]);

  useEffect(() => {
    if (activeShareLinks.content.length > 0) {
      setLinksActive(true);
    }
  }, [activeShareLinks.content.length]);

  // после генерации ссылки обновляет url и qr код в сторе
  useEffect(() => {
    if (studentShareLink?.loading) return;
    const linkUrl = studentShareLink?.content?.url;
    const qrCode = studentShareLink?.content?.qrCode;

    dispatch(linkSharingDrawerActions.setCurrentLink(linkUrl, qrCode));
  }, [dispatch, studentShareLink?.content?.qrCode, studentShareLink?.content?.url, studentShareLink?.loading]);

  const handleUpdateLinkActivity = (isActive: boolean) => {
    setLinksActive(isActive);
    if (currentStudent?.meshId) {
      const linkActivityRequest: IUpdateLinkActivityRequest = { isActive };
      updateStudentShareLinkActivity(currentStudent?.meshId, linkActivityRequest);
    }
  };

  const handleChangeCurrentTab = (tab: LinkSharingTabs) => {
    setCurrentTab(tab);
  };

  const handleOpenLinkUrl = () => {
    generateLink('link');
    dispatch(linkSharingDrawerActions.setCurrentLink(undefined, undefined));
    dispatch(linkSharingDrawerActions.setLinkUrlOpen(true));
  };

  const handleOpenLinkQrCode = () => {
    generateLink('qr');
    dispatch(linkSharingDrawerActions.setCurrentLink(undefined, undefined));
    dispatch(linkSharingDrawerActions.setLinkQrCodeOpen(true));
  };

  const handleOpenActiveLinks = () => {
    emitYMEvent({
      type: 'sharePortfolio_activeLinks_open_click',
    });
    setActiveLinksOpen(true);
  };

  const handleCloseActiveLinks = () => {
    setActiveLinksOpen(false);
  };

  // Если выносить компонент отдельно, то реакт будет жаловаться на потерю стора. Решения лучше пока не найдено
  // TODO попробовать вынести компонент отдельно
  const PdfPageTemplate: FC<PageTemplateProps> = ({ pageNum, totalPages }) => {
    const currentDate = new Date();
    const formattedDate = `Сохранено ${format(currentDate, 'dd.MM.yyyy')} в ${currentDate.getHours()}:${
      (currentDate.getMinutes() < 10 ? '0' : '') + currentDate.getMinutes()
    }`;

    return (
      <div className="pdf-page-template">
        {pageNum !== 1 && (
          <>
            <div className="pdf-info">
              <img src={meshPortfolioLogo} alt="mesh" />
              <div className="pdf-info__creation-date">{formattedDate}</div>
            </div>
            <div className="pdf-paging">
              <div className="pdf-paging__user-info">
                {rawDisplaySettings?.studentData && (
                  <>
                    Портфолио{' '}
                    <span className="pdf-paging__user-fio">
                      {currentStudent?.lastname} {currentStudent?.firstname}
                    </span>
                  </>
                )}
              </div>
              <div className="pdf-paging__current-page">{`стр. ${pageNum - 1} из ${totalPages - 1}`}</div>
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (currentStudent.meshId) getStudentShareLinkList(currentStudent.meshId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStudent]);

  const generateLink = (type: 'link' | 'qr') => {
    const isCustomPeriod = linkExpiration === LinkExpirationId.period;

    if (
      isCustomPeriod &&
      customPeriodStart &&
      customPeriodEnd &&
      format(customPeriodStart, 'yyyy-MM-dd') === format(customPeriodEnd, 'yyyy-MM-dd')
    ) {
      setCustomPeriodEnd(new Date(customPeriodEnd.setHours(23, 59, 59)));
    }

    if (
      currentStudent &&
      currentStudent.meshId &&
      themeSettings.code &&
      (!isCustomPeriod || (customPeriodStart && customPeriodEnd))
    ) {
      const periodStartDate = customPeriodStart ?? today;
      const periodEndDate = customPeriodEnd ?? weekLater;

      const period: Pick<ICreateLinkRequest, 'accessPeriod' | 'customPeriod'> = isCustomPeriod
        ? {
            customPeriod: {
              startDate: format(periodStartDate, "yyyy-MM-dd'T'00:00:00.000'Z'"),
              endDate: format(periodEndDate, "yyyy-MM-dd'T'00:00:00.000'Z'"),
            },
          }
        : {
            accessPeriod: linkExpiration,
          };

      const request: ICreateLinkRequest = {
        display: fullRawSettings,
        themeSetting: themeSettings.code,
        ...period,
      };

      const expirationIdToNameMapping = {
        [LinkExpirationId.week]: LinkExpiration.week,
        [LinkExpirationId.mounth]: LinkExpiration.mounth,
        [LinkExpirationId.year]: LinkExpiration.year,
        [LinkExpirationId.period]: LinkExpiration.period,
      };
      const ymMeta = getYMEventPayload({
        type: 'linkSettingCopy',
        payload: {
          type,
          linkAccess: areLinksActive ? 'on' : 'off',
          includedSections: getMetrikaEnabledLinkSettingsSections(viewSettings),
          linkPeriod: expirationIdToNameMapping[linkExpiration],
        },
      });

      createStudentShareLink(currentStudent?.meshId, request, ymMeta);
    }
  };

  const customPeriodController: DayRangePickerController = {
    handleChange(value) {
      setCustomPeriodStart(value.start);
      setCustomPeriodEnd(value.end);
    },
    handleClear() {
      setCustomPeriodStart(today);
      setCustomPeriodEnd(weekLater);
    },
  };

  const isDisplaySettingsSelected = viewSettings.find((res) => res.isActive === true);
  const isDownloadBtnDisabled =
    !isDisplaySettingsSelected || portfolioContentLoading || isPdfLoading || academicYearSelectValue.length === 0;

  const exportPDFWithMethod = () => {
    emitYMEvent({
      type: 'downloadButtonClick',
      payload: {
        includedSections: getMetrikaEnabledLinkSettingsSections(viewSettings),
        formatChoice: activeFormat,
        yearsChoice: chosenAcademicYearsList,
      },
    });

    toast.success('Загрузка файла началась');
    setPdfLoading(true);
    const element = pdfContainer.current || document.body;
    savePDF(
      element,
      {
        pageTemplate: PdfPageTemplate,
        paperSize: 'A4',
        fileName: 'portfolio.pdf',
        keepTogether: '.pdf-keep',
        scale: 0.48,
        author: 'student',
        proxyURL: `/portfolio/app/persons/${currentStudent?.meshId}/printPDF`,
        forceProxy: !isFirefox,
        proxyData: {
          bearer: `Bearer ${getAuthToken()}`,
        },
        landscape: activeFormat === 'vertical' ? false : true,
      },
      () => {
        emitYMEvent({ type: 'downloadButtonSuccessful' });

        toast.success('Загрузка файла завершена');
        setPdfLoading(false);
      },
    );
  };

  const isCustomPeriodError =
    !customPeriodStart ||
    !customPeriodEnd ||
    isBefore(customPeriodStart, today) ||
    isBefore(customPeriodEnd, today) ||
    isBefore(customPeriodEnd, customPeriodStart);

  const isLinkTab = currentTab === LinkSharingTabs.LINK;
  const isFileTab = currentTab === LinkSharingTabs.FILE;

  const isMobileActiveLinksOpen = isMobile && isActiveLinksOpen;
  const accentColor = getAccentColor(theme.palette.accentColor, '100');

  const noDataPlugElement = portfolioContentLoading ? (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'center' }}
    >
      <MoonLoader size={75} color={accentColor} />
    </Box>
  ) : (
    <Box sx={styles.noDataPlug}>
      <Box component="img" src={noData} alt="Нет ссылок" sx={{ width: '213px', paddingBlock: '8px' }} />
      <Box style={{ textAlign: 'center' }}>
        <LmText variant="h6">Здесь пока ничего нет</LmText>
        <LmText variant="lg" color="note">
          В твоем портфолио пока нет информации
        </LmText>
      </Box>
    </Box>
  );

  const desktopTabsElement = (
    <UnderlinedTabs value={currentTab} onChange={handleChangeCurrentTab} sx={{ marginTop: '24px' }}>
      <UnderlinedTabs.Button value={LinkSharingTabs.LINK} content="Доступ по ссылке" />
      <UnderlinedTabs.Button value={LinkSharingTabs.FILE} content="Скачать файл" />
    </UnderlinedTabs>
  );

  const mobileTabsElement = (
    <Switcher
      value={currentTab}
      onChange={handleChangeCurrentTab}
      componentSize="large"
      sx={{ display: 'flex', flexDirection: 'row', width: '100%', '& button': { flexGrow: 1, flexBasis: '1px' } }}
    >
      <SwitcherButton value={LinkSharingTabs.LINK} content="По ссылке" />
      <SwitcherButton value={LinkSharingTabs.FILE} content="Скачать файл" />
    </Switcher>
  );

  const desktopHeader: IDrawerBase['header'] = (crossButton) => (
    <DrawerHeader
      title="Поделиться портфолио"
      backgroundImage={isBrightTheme ? cardIllustrations.scienceReward : null}
      accentColor="indigo"
      crossButton={crossButton}
      removeBottomPadding
    >
      {desktopTabsElement}
    </DrawerHeader>
  );

  const mobileHeader: IDrawerBase['header'] = (crossButton) => (
    <MobileDrawerHeader
      accentColor="indigo"
      crossButton={crossButton}
      title={isMobileActiveLinksOpen ? `Активные ссылки (${activeShareLinks.content.length})` : 'Поделиться портфолио'}
      onExitBack={isMobileActiveLinksOpen ? handleCloseActiveLinks : undefined}
    />
  );

  const footer = (
    <GroupedActionButtonsContainer disableAbsolutePosition>
      {isLinkTab && (
        <GroupedActionButton
          variant="primary"
          disabled={studentShareLink?.loading || !isDisplaySettingsSelected || isCustomPeriodError}
          startIcon={IconCopyOutline}
          handleClick={handleOpenLinkUrl}
        >
          {isMobile ? 'Ссылка' : 'Копировать ссылку'}
        </GroupedActionButton>
      )}
      {isLinkTab && (
        <GroupedActionButton
          disabled={studentShareLink?.loading || !isDisplaySettingsSelected}
          startIcon={IconQr}
          handleClick={handleOpenLinkQrCode}
        >
          {isMobile ? 'QR-код' : 'Показать QR-код'}
        </GroupedActionButton>
      )}
      {isFileTab && (
        <GroupedActionButton
          disabled={isDownloadBtnDisabled}
          startIcon={IconDownloadFile}
          handleClick={exportPDFWithMethod}
        >
          Скачать файл
        </GroupedActionButton>
      )}
    </GroupedActionButtonsContainer>
  );

  const drawerDefaultContent = (
    <>
      {isMobile && <Box sx={styles.mobileTabs}>{mobileTabsElement}</Box>}
      <Box sx={styles.widgetsContainer(isFileTab)}>
        {isLinkTab && (
          <LinksOverview
            isAccessEnabled={areLinksActive}
            onChangeAccess={handleUpdateLinkActivity}
            onOpenLinks={handleOpenActiveLinks}
          />
        )}

        {isFileTab && (
          <>
            <AcademicYear currentTab={currentTab} />
            <Format />
          </>
        )}

        <Settings currentTab={currentTab} settings={viewSettings} />

        {isLinkTab && (
          <LinksPeriod
            periodType={linkExpiration}
            onChangePeriodType={setLinkExpiration}
            customPeriodStart={customPeriodStart}
            customPeriodEnd={customPeriodEnd}
            customPeriodController={customPeriodController}
            isCustomPeriodError={isCustomPeriodError}
          />
        )}
      </Box>
    </>
  );

  const drawerMobileActiveLinksContent = <ActiveLinksList activeLinks={activeShareLinks.content} />;

  return (
    <>
      <Drawer
        open={isOpen}
        isFullHeight
        isMobile={isMobile}
        anchor={isMobile ? 'bottom' : 'right'}
        swipeable={isMobile}
        swipeableProps={{
          onClose,
        }}
        header={isMobile ? mobileHeader : desktopHeader}
        footer={!isMobileActiveLinksOpen && footer}
        onClose={onClose}
        sx={styles.drawerRoot}
        contentSx={styles.drawerContent}
      >
        {(isFileTab && academicYears.length === 0) || portfolioContentLoading ? (
          noDataPlugElement
        ) : (
          <>
            {/* overflow: auto переносится с контента дровера на внутренний элемент */}
            <Box className="link-sharing-form-container" sx={styles.drawerInnerContent}>
              <Box
                ref={sharingContainerRef}
                className="link-sharing-form"
                sx={mergeSx(drawerContentPadding(true), styles.root)}
              >
                {isMobileActiveLinksOpen ? drawerMobileActiveLinksContent : drawerDefaultContent}

                {!isMobile && (
                  <ActiveLinksPopUp
                    isOpen={isActiveLinksOpen}
                    onClose={handleCloseActiveLinks}
                    activeLinks={activeShareLinks.content}
                  />
                )}
              </Box>
            </Box>
            {isOpen && isFileTab && (
              <Box ref={pdfContainer}>
                <StudentPortfolioPdf display={fullRawSettings} />
              </Box>
            )}
            {isOpen && <SectionsDataLoader />}
          </>
        )}
      </Drawer>
    </>
  );
};

export default connect(
  (state: IRootState) => ({
    currentStudent: state.currentStudent,
    studentShareLink: state.studentShareLink,
    adminVisibilitySettings: adminSettingsSelector(state),
    themeSettings: state.userThemeSettings,
    // TODO сделать нормальное получение состояния загрузки из пдф модуля
    // предположительно сделать как поиске
    portfolioContentLoading:
      state.studentEvents.loading ||
      state.studentProjects.loading ||
      state.studentAffilations.loading ||
      state.studentEmployments.loading ||
      state.studentRewards.loading ||
      state.studentSportRewards.loading ||
      state.studentGiaList.loading ||
      state.studentFinalGrades.loading ||
      state.studentAverageGrades.loading ||
      state.studentDiagnosis?.loading ||
      state.studentShareLink.loading ||
      state.personsObjects.loading ||
      state.cultureVisitings.loading ||
      state.personalDiagnostics.loading,
  }),
  {
    getStudentShareLinkList: getStudentShareLinkListActions.request,
    createStudentShareLink: createStudentShareLinkActions.request,
    updateStudentShareLinkActivity: updateStudentShareLinkActivityActions.request,
    getStudentDiagnosis: getStudentDiagnosisActions.request,
  },
)(LinkSharingForm);
